import { Attribute, CalculatedPlayer, Colours, getNationData, LookupDetails, NationalLevel, Rarity, StatBlock } from "@alienheadwars/football-card-manager-model";
import '@fontsource/norwester';
import { Box, CardContent, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { collectionManager } from "../../../collection/collectionManager";
import { localise } from "../../../locale/locale";
import { Icon, IconId } from "../../base/icon/Icon";
import IconisedString from "../../base/icon/IconisedString";
import { HelpOverlay } from "../../base/tutorial/helpOverlay/HelpOverlay";
import { IMPACT_FONTS } from "../../fonts/fonts";
import { AnimatedBox, AnimatedCard, AnimatedStack } from "../../MotionMui/MotionMui";
import { InlineCardComponent } from "../InlineCard";
import { ALL_ATTRIBUTES } from "./AttributeSets";
import { Avatar, AvatarComponentProps } from "./avatarComponents/Avatar";
import { CardDimensions, LargeCard } from "./CardDimensions";
import { PlayerRaceIcon } from "./PlayerRaceIcon";
import { RarityGlow } from "./RarityGlow";


type DetailType = 'ROLE' | 'NATION' | 'RACE';

interface PlayerCardDisplayContextProps {
    selectedDetail: DetailType | null;
    setSelectedDetail: React.Dispatch<React.SetStateAction<DetailType | null>>;
}

const PlayerCardDisplayContext = React.createContext<PlayerCardDisplayContextProps | undefined>(undefined);

const PlayerCardDisplayProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [selectedDetail, setSelectedDetail] = useState<DetailType | null>(null);

    return (
        <PlayerCardDisplayContext.Provider value={{ selectedDetail, setSelectedDetail }}>
            {children}
        </PlayerCardDisplayContext.Provider>
    );
};

const usePlayerCardDisplay = () => {
    const context = React.useContext(PlayerCardDisplayContext);
    if (context === undefined) {
        throw new Error('usePlayerCardDisplay must be used within a PlayerCardDisplayProvider');
    }
    return context;
};


interface PlayerCardComponentProps {
    player: LookupDetails;
    attributeSet?: Attribute[];
    cardDimensions?: CardDimensions;
    onClick?(): void;
    showTutorials?: boolean;
    inLine?: boolean;
}

export const PlayerCardComponent = (props: PlayerCardComponentProps) => {
    const cardDimensions = props.cardDimensions ?? LargeCard;
    const showTutorials = props.showTutorials ?? false;

    const [player, setPlayer] = useState<CalculatedPlayer | undefined>(undefined);

    const getPlayer = useCallback(async () => {
        if ((props.player as CalculatedPlayer).combinedBonus) {
            return props.player as CalculatedPlayer;
        }
        const calculatedPlayer = await collectionManager.getCalculatedPlayer(props.player)
        return calculatedPlayer ? { ...calculatedPlayer, ...props.player } : undefined;
    }, [props.player]);

    useEffect(() => {
        getPlayer().then(p => { if (p) setPlayer(p); });
    }, [props.player, getPlayer]);


    if (!player) {
        return <></>
    }
    if (props.inLine || (props.inLine === undefined && cardDimensions.defaultInLine)) {
        return <InlinePlayerCardComponent player={player} dimensions={cardDimensions} onClick={props.onClick} />
    }

    return (
        <PlayerCardDisplayProvider>
            <CardFrame colours={player.colours} dimensions={cardDimensions} onclick={props.onClick}>
                {cardDimensions.showRarityGlow && <RarityGlow rarity={player.rarity} style={{ borderRadius: cardDimensions.cardBorderRadius }} />}
                <StatsFrame player={player} dimensions={cardDimensions} attributeSet={props.attributeSet ?? ALL_ATTRIBUTES} showTutorials={showTutorials} />
                <Artwork player={player} dimensions={cardDimensions} showTutorials={showTutorials} />
            </CardFrame>
        </PlayerCardDisplayProvider>
    );
}

interface inlinePlayerProps {
    player: CalculatedPlayer;
    dimensions: CardDimensions;
    onClick?(): void;
}

export const InlinePlayerCardComponent = (props: inlinePlayerProps) => {
    const { dimensions, player } = props;
    const colours = player.colours
    const avatar=<Artwork player={player} dimensions={dimensions} showTutorials={false} />
    return <InlineCardComponent dimensions={dimensions} onClick={props.onClick} avatar={avatar} title={localise(`player.${player.id}`)} colours={colours} />
};

interface CardFrameProps {
    children: React.ReactNode;
    colours: Colours;
    dimensions: CardDimensions;
    onclick?(): void;
}

const CardFrame = (props: CardFrameProps) => {
    const { dimensions, children, colours, onclick } = props;

    return (
        <AnimatedCard
            onClick={onclick}
            sx={{
                width: dimensions.cardWidth,
                position: 'relative',
                justifyItems: 'center',
                margin: dimensions.cardMargin,
                cursor: onclick ? 'pointer' : 'default',
                backgroundColor: colours.secondaryColour,
                borderRadius: dimensions.cardBorderRadius,
                overflow: 'visible',
                flexGrow: 0,
                flexShrink: 0,
            }}
        >
            {children}
        </AnimatedCard>
    );
};


interface StatsFrameProps {
    player: CalculatedPlayer;
    dimensions: CardDimensions;
    attributeSet: Attribute[];
    showTutorials: boolean;
}

const StatsFrame = (props: StatsFrameProps) => {
    const { dimensions, player, showTutorials } = props;
    return <AnimatedBox
        style={{
            margin: dimensions.statsFrameMargin,
            width: dimensions.statsFrameWidth,
            border: `${dimensions.borderWidth} solid ${player.colours.primaryColour}`,
            borderRadius: dimensions.cardBorderRadius,
            zIndex: 101,
            position: 'relative',
            alignItems: 'center',
            justifyContent: 'center',
            display: 'flex',
            flexDirection: 'column',
        }}
    >
        <Box sx={{ height: dimensions.statsFrameInnerHeight }} />
        {dimensions.showAttributes && <PlayingStats player={player} dimensions={dimensions} attributeSet={props.attributeSet} showTutorials={showTutorials} />}
        {dimensions.showDetails && <PlayingDetails player={player} dimensions={dimensions} showTutorials={showTutorials} />}
        {dimensions.showName && <PlayerName
            id={player.id}
            colours={player.colours}
            dimensions={dimensions}
            sx={dimensions.showDescription ? {} : { marginBottom: dimensions.playerNameMarginBottom }}
        />}
        {dimensions.showDescription &&
            <AnimatedBox>
                <PlayerDescriptionComponent player={player} dimensions={dimensions} showTutorials={showTutorials} />
            </AnimatedBox>
        }
    </AnimatedBox>;
};

interface PlayerDescriptionComponentProps {
    player: CalculatedPlayer;
    dimensions: CardDimensions;
    showTutorials: boolean;
}

const PlayerDescriptionComponent = (props: PlayerDescriptionComponentProps) => {
    const { dimensions, player, showTutorials } = props;
    const { selectedDetail } = usePlayerCardDisplay();

    if (selectedDetail === 'ROLE') {
        return <PlayerRoleDescription player={player} dimensions={dimensions} showTutorials={showTutorials} />
    }
    if (selectedDetail === 'NATION') {
        return <PlayerNationalityDescription player={player} dimensions={dimensions} showTutorials={showTutorials} />
    }
    if (selectedDetail === 'RACE') {
        return <PlayerRaceDescription player={player} dimensions={dimensions} showTutorials={showTutorials} />
    }
    return <PlayerTraitsList player={player} dimensions={dimensions} showTutorials={showTutorials} />
}

const StatBonuses = (props: { statBlock: StatBlock, colours: Colours }) => {
    return <>{props.statBlock &&
        Object.entries(props.statBlock)
            .filter(([stat, value]) => value !== undefined)
            .map(([stat, value]) => (
                <IconisedString
                    key={stat}
                    string={` +${value}_${stat.toUpperCase()}_`}
                    iconProps={{
                        fontSize: 'inherit',
                        primaryColor: props.colours.primaryTextColour,
                        secondaryColor: props.colours.primaryBackgroundColour,
                    }}
                />
            ))}</>
}

interface PlayerRoleDescriptionProps extends PlayerDescriptionComponentProps { }

const PlayerRoleDescription = (props: PlayerRoleDescriptionProps) => {
    const { dimensions, player } = props;
    const colours = player.colours;

    return (
        <AnimatedCard
            sx={{
                margin: dimensions.descriptionCardMargin,
                backgroundColor: colours.primaryBackgroundColour,
                padding: dimensions.traitCardPadding
            }}
        >
            <CardContent
                style={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0
                }}>
                <Typography
                    sx={{
                        color: colours.primaryTextColour,
                        fontSize: dimensions.playerDescriptionHeaderFontSize,
                    }}>
                    {localise(`playerroles.${player.roleId}`)}
                </Typography>
                <AnimatedStack spacing={1}>
                    {
                        player.roleBonus && Object.keys(player.roleBonus.statBlock ?? {}).length > 0 && <>
                            <Typography variant="h6" component="p" style={{
                                color: colours.primaryTextColour,
                                fontSize: dimensions.playerDescriptionBodyFontSize,
                                opacity: 0.3
                            }}>
                                {localise('playerroles.bonuses')}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: colours.primaryTextColour,
                                    fontSize: dimensions.playerDescriptionBodyFontSize,
                                    lineHeight: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                <StatBonuses statBlock={player.roleBonus.statBlock} colours={colours} />
                            </Typography>
                        </>}
                    {player.roleBonus.playerTraits.length > 0 && <>
                        <Typography variant="h6" component="p" style={{
                            fontSize: dimensions.playerDescriptionBodyFontSize,
                            opacity: 0.3,
                            color: colours.primaryTextColour,
                        }}>{localise('playerroles.traits')}</Typography>
                        {player.roleBonus.playerTraits.map((trait) => (
                            <Typography variant="body2" style={{
                                fontSize: dimensions.playerDescriptionBodyFontSize,
                                color: colours.primaryTextColour,
                            }} key={trait}>
                                <span key={trait}>
                                    <b>{localise(`playertraits.${trait}`)},</b> {localise(`playertraits.${trait}.bonuses`)}
                                </span>
                            </Typography>
                        ))}
                    </>
                    }
                </AnimatedStack>
            </CardContent>
        </AnimatedCard>
    );
}

interface PlayerRaceDescriptionProps extends PlayerDescriptionComponentProps { }
const PlayerRaceDescription = (props: PlayerRaceDescriptionProps) => {
    const { dimensions, player } = props;
    const colours = player.colours;

    return (
        <AnimatedCard
            sx={{
                margin: dimensions.descriptionCardMargin,
                backgroundColor: colours.primaryBackgroundColour,
                padding: dimensions.traitCardPadding
            }}
        >
            <CardContent
                style={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0
                }}>
                <Typography
                    sx={{
                        color: colours.primaryTextColour,
                        fontSize: dimensions.playerDescriptionHeaderFontSize,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <PlayerRaceIcon
                        raceId={player.raceId}
                        width={dimensions.descriptionHeaderIconWidth} // e.g., '24px' or a variable
                        color={colours.primaryTextColour} // Or dynamically set based on background: isDark ? 'white' : 'black'
                        sx={{ marginRight: '1vmin' }} // Add margin via sx prop
                    />
                    {localise(`races.${player.raceId}`)}
                </Typography>
                <AnimatedStack spacing={1}>
                    {
                        player.racialBonus && Object.keys(player.racialBonus.statBlock ?? {}).length > 0 && <>
                            <Typography variant="h6" component="p" style={{
                                color: colours.primaryTextColour,
                                fontSize: dimensions.playerDescriptionBodyFontSize,
                                opacity: 0.3
                            }}>
                                {localise('races.bonuses')}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: colours.primaryTextColour,
                                    fontSize: dimensions.playerDescriptionBodyFontSize,
                                    lineHeight: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                <StatBonuses statBlock={player.racialBonus.statBlock} colours={colours} />
                            </Typography>
                        </>}
                    {player.racialBonus.playerTraits.length > 0 && <>
                        <Typography variant="h6" component="p" style={{
                            fontSize: dimensions.playerDescriptionBodyFontSize,
                            opacity: 0.3,
                            color: colours.primaryTextColour,
                        }}>{localise('races.traits')}</Typography>
                        {player.racialBonus.playerTraits.map((trait) => (
                            <Typography variant="body2" style={{
                                fontSize: dimensions.playerDescriptionBodyFontSize,
                                color: colours.primaryTextColour,
                            }} key={trait}>
                                <span key={trait}>
                                    <b>{localise(`playertraits.${trait}`)},</b> {localise(`playertraits.${trait}.bonuses`)}
                                </span>
                            </Typography>
                        ))}
                    </>
                    }
                </AnimatedStack>
            </CardContent>
        </AnimatedCard>
    );
}

interface PlayerNationalityDescriptionProps extends PlayerDescriptionComponentProps { }
const PlayerNationalityDescription = (props: PlayerNationalityDescriptionProps) => {
    const { dimensions, player } = props;
    const colours = player.colours;
    const nationData = getNationData(player.national.nationId);

    return (
        <AnimatedCard
            sx={{
                margin: dimensions.descriptionCardMargin,
                backgroundColor: colours.primaryBackgroundColour,
                padding: dimensions.traitCardPadding
            }}
        >
            <CardContent
                style={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0
                }}>
                <Typography
                    sx={{
                        color: colours.primaryTextColour,
                        fontSize: dimensions.playerDescriptionHeaderFontSize,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <img
                        src={`/images/flags/192x144/${player.national.nationId}.png`}
                        alt={`${player.national.nationId} flag`}
                        style={{ width: dimensions.descriptionHeaderIconWidth, height: 'auto', marginRight: '1vmin' }}
                    />
                    {localise(`nationalities.${player.national.nationId}`)}
                    {player.national.nationalLevel && player.national.nationalLevel !== NationalLevel.BASIC && ` ${localise(`national.level.${player.national.nationalLevel}`)} `}
                </Typography>
                <AnimatedStack spacing={1}>
                    {
                        player.nationBonus && Object.keys(player.nationBonus.statBlock ?? {}).length > 0 && <>
                            <Typography variant="h6" component="p" style={{
                                color: colours.primaryTextColour,
                                fontSize: dimensions.playerDescriptionBodyFontSize,
                                opacity: 0.3
                            }}>
                                {localise('national.bonuses')}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: colours.primaryTextColour,
                                    fontSize: dimensions.playerDescriptionBodyFontSize,
                                    lineHeight: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                <StatBonuses statBlock={player.nationBonus.statBlock} colours={colours} />
                            </Typography>
                        </>}
                    <Typography variant="h6" component="p" style={{
                        color: colours.primaryTextColour,
                        fontSize: dimensions.playerDescriptionBodyFontSize,
                        opacity: 0.3
                    }}>
                        {localise('national.favoured-enemy')}</Typography>
                    {player.nationBonus.preferredOpponent!.map(nation => <Typography variant="body2" component="p" style={{
                        fontSize: dimensions.playerDescriptionBodyFontSize,
                        color: colours.primaryTextColour,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }} key={nation}>
                        +4 vs <img
                            src={`/images/flags/192x144/${nation}.png`}
                            alt={`${nation} flag`}
                            style={{ height: dimensions.descriptionBodyIconHeight, width: 'auto', margin: '0 0.5vmin' }}
                        />{localise(`nationalities.${nation}`)}
                    </Typography>
                    )}
                    {player.national.nationalLevel !== NationalLevel.BASIC ? <>
                        <Typography variant="h6" component="p" style={{
                            color: colours.primaryTextColour,
                            fontSize: dimensions.playerDescriptionBodyFontSize,
                            opacity: 0.3
                        }}>
                            {localise('national.hero-trait')}</Typography>
                        <Typography variant="body2" component="p" style={{
                            fontSize: dimensions.playerDescriptionBodyFontSize,
                            color: colours.primaryTextColour,
                        }}>
                            <b>{localise(`playertraits.${nationData.nationalTrait}`)},</b> {localise(`playertraits.${nationData.nationalTrait}.bonuses`)}</Typography>
                    </> : ''
                    }
                    {player.national.nationalLevel === NationalLevel.ICON ? <>
                        <Typography variant="h6" component="p" style={{
                            fontSize: dimensions.playerDescriptionBodyFontSize, opacity: 0.3,
                            color: colours.primaryTextColour,
                        }}>
                            {localise('national-icon')}</Typography>
                        <Typography variant="body2" component="p" style={{
                            fontSize: dimensions.playerDescriptionBodyFontSize,
                            color: colours.primaryTextColour,
                        }}>
                            {localise(`playertraits.ICON.bonuses`)}</Typography>
                    </> : ''}
                </AnimatedStack>
            </CardContent>
        </AnimatedCard>
    );
}

interface PlayerTraitsListProps extends PlayerDescriptionComponentProps { }

const PlayerTraitsList = (props: PlayerTraitsListProps) => {
    const { dimensions, player, showTutorials } = props;
    const colours = props.player.colours;

    return (
        <AnimatedStack
            spacing={dimensions.traitsListSpacing}
            sx={{
                margin: dimensions.descriptionCardMargin,
            }}>
            <HelpOverlay
                helpText={localise('playerCard.favouredOpponent.help')}
                tutorialId={'playerCard.favouredOpponent.helpa'}
                additionalCondition={showTutorials}
            >
                <AnimatedCard key={'favouredOpponent'} sx={{
                    backgroundColor: colours.primaryBackgroundColour,
                    padding: dimensions.traitCardPadding
                }}>
                    <Typography variant="body2" style={{
                        fontSize: dimensions.playerDescriptionBodyFontSize,
                        color: colours.primaryTextColour,
                    }} >

                        +4 vs {player.combinedBonus.preferredOpponent!.map(nation => {
                            return <img
                                src={`/images/flags/192x144/${nation}.png`}
                                alt={`${nation} flag`}
                                style={{ height: dimensions.descriptionBodyIconHeight, width: 'auto', margin: '0 0.5vmin' }}
                            />
                        })
                        }
                    </Typography>
                </AnimatedCard>
            </HelpOverlay>
            {player.combinedBonus.playerTraits.map((trait, index) => {
                return (
                    <HelpOverlay
                        helpText={localise('playerCard.trait.generic.help')}
                        tutorialId={'playerCard.trait.generic.help'}
                        additionalCondition={showTutorials && index === 0}
                    >
                        <AnimatedCard key={trait} sx={{
                            backgroundColor: colours.primaryBackgroundColour,
                            padding: dimensions.traitCardPadding
                        }}>
                            <Typography variant="body2" style={{
                                fontSize: dimensions.playerDescriptionBodyFontSize,
                                color: colours.primaryTextColour,
                            }} >
                                <span key={trait}>
                                    <b>{localise(`playertraits.${trait}`)},</b> {localise(`playertraits.${trait}.bonuses`)}
                                </span>
                            </Typography>
                        </AnimatedCard>
                    </HelpOverlay>
                );
            })}
        </AnimatedStack>
    );
}

interface PlayingDetailsProps {
    player: CalculatedPlayer;
    dimensions: CardDimensions;
    showTutorials: boolean;
}
const PlayingDetails = (props: PlayingDetailsProps) => {
    const { dimensions, player, showTutorials } = props;
    const { selectedDetail, setSelectedDetail } = usePlayerCardDisplay();

    const handleBubbleClick = (event: React.MouseEvent<HTMLElement>, detailType: DetailType) => {
        if (dimensions.showDescription) {
            event.stopPropagation();
            setSelectedDetail(prev => prev === detailType ? null : detailType);
        }
    };

    const selectedSx = {
        backgroundColor: player.colours.primaryColour,
        borderColor: player.colours.primaryBackgroundColour,
    };

    const isClickable = !!dimensions.showDescription && !!dimensions.switchDetails;
    const isRoleSelected = isClickable && selectedDetail === 'ROLE';
    const isNationSelected = isClickable && selectedDetail === 'NATION';
    const isRaceSelected = isClickable && selectedDetail === 'RACE';

    const isAdvancedRace = player.raceId !== 'HUMAN';
    const isAdvancedRole = player.roleLevel >= 1;
    const isAdvancedNation = player.national.nationalLevel === NationalLevel.HERO || player.national.nationalLevel === NationalLevel.ICON;

    const roleHelpKey = 'playerCard.baseRole.help';
    const advancedRoleHelpKey = 'playerCard.advancedRole.help';
    const nationHelpKey = 'playerCard.nation.help';
    const advancedNationHelpKey = 'playerCard.advancedNation.help';
    const raceHelpKey = 'playerCard.race.help';
    const advancedRaceHelpKey = 'playerCard.advancedRace.help';


    return (
        <AnimatedBox
            sx={{
                right: dimensions.playingDetailsRightOffset,
                top: dimensions.playingDetailsTopOffset,
                overflow: 'visible',
                position: 'absolute',
            }}
        >
            <AnimatedStack
                spacing={dimensions.playingDetailsStackSpacing}
                sx={{
                    width: 0,
                    alignItems: 'center',
                    justifyItems: 'center',
                    justifyContent: 'center',
                    alignContent: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <HelpOverlay
                    helpText={localise(roleHelpKey)}
                    tutorialId={roleHelpKey}
                    additionalCondition={showTutorials && !isAdvancedRole && selectedDetail !== 'ROLE'}
                >
                    <HelpOverlay
                        helpText={localise(advancedRoleHelpKey)}
                        tutorialId={advancedRoleHelpKey}
                        additionalCondition={showTutorials && isAdvancedRole && selectedDetail !== 'ROLE'}
                    >
                        <AttributeBubble
                            key='BASE_ROLE'
                            colours={player.colours}
                            dimensions={dimensions}
                            diameter={dimensions.attributeBubbleDiameter}
                            onClick={isClickable ? (event) => handleBubbleClick(event, 'ROLE') : undefined}
                            sx={isRoleSelected ? selectedSx : {}}
                        >
                            <Typography sx={{
                                lineHeight: '1',
                                color: player.colours.primaryTextColour,
                                fontSize: dimensions.playerStatFontSize,
                                textWrap: 'nowrap',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <b>{localise(`baserole.shortHand.${player.baseRole}`)}{player.roleLevel >= 1 && '+'}{player.roleLevel >= 2 && '+'}</b>
                            </Typography>
                        </AttributeBubble>
                    </HelpOverlay>
                </HelpOverlay>

                <HelpOverlay
                    helpText={localise(nationHelpKey)}
                    tutorialId={nationHelpKey}
                    additionalCondition={showTutorials && !isAdvancedNation && selectedDetail !== 'NATION'}
                >
                    <HelpOverlay
                        helpText={localise(advancedNationHelpKey)}
                        tutorialId={advancedNationHelpKey}
                        additionalCondition={showTutorials && isAdvancedNation && selectedDetail !== 'NATION'}
                    >
                        <AttributeBubble
                            key='NATION_FLAG'
                            colours={player.colours}
                            dimensions={dimensions}
                            diameter={dimensions.attributeBubbleDiameter}
                            onClick={isClickable ? (event) => handleBubbleClick(event, 'NATION') : undefined}
                            sx={isNationSelected ? selectedSx : {}}
                        >
                            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                                <img
                                    src={`/images/flags/192x144/${player.nationId}.png`}
                                    alt={`${player.nationId} flag`}
                                    style={{ width: dimensions.playingDetailsNationIconWidth }}
                                />
                                {(player.national?.nationalLevel === NationalLevel.HERO || player.national?.nationalLevel === NationalLevel.ICON) && (
                                    <Typography sx={{
                                        transform: 'translate(0%, 5%)',
                                        position: 'absolute',
                                        color: player.colours.primaryTextColour,
                                        fontSize: dimensions.playingDetailsNationHeroIconFontSize,
                                        fontWeight: 'bold',
                                        lineHeight: 1,
                                        fontFamily: IMPACT_FONTS,
                                        pointerEvents: 'none',
                                        // Add textShadow for the outline
                                        textShadow: `
                -${dimensions.heroIconOutlineWidth} -${dimensions.heroIconOutlineWidth} 0 ${player.colours.primaryColour},  
                 ${dimensions.heroIconOutlineWidth} -${dimensions.heroIconOutlineWidth} 0 ${player.colours.primaryColour},
                -${dimensions.heroIconOutlineWidth}  ${dimensions.heroIconOutlineWidth} 0 ${player.colours.primaryColour},
                 ${dimensions.heroIconOutlineWidth}  ${dimensions.heroIconOutlineWidth} 0 ${player.colours.primaryColour}
            `
                                    }}>
                                        {player.national.nationalLevel === NationalLevel.HERO ? 'H' : 'I'}
                                    </Typography>
                                )}
                            </Box>
                        </AttributeBubble>
                    </HelpOverlay>
                </HelpOverlay>

                <HelpOverlay
                    helpText={localise(raceHelpKey)}
                    tutorialId={raceHelpKey}
                    additionalCondition={showTutorials && !isAdvancedRace && selectedDetail !== 'RACE'}
                >
                    <HelpOverlay
                        helpText={localise(advancedRaceHelpKey)}
                        tutorialId={advancedRaceHelpKey}
                        additionalCondition={showTutorials && isAdvancedRace && selectedDetail !== 'RACE'}
                    >
                        <AttributeBubble
                            key='RACE'
                            colours={player.colours}
                            dimensions={dimensions}
                            diameter={dimensions.attributeBubbleDiameter}
                            onClick={isClickable ? (event) => handleBubbleClick(event, 'RACE') : undefined}
                            sx={isRaceSelected ? selectedSx : {}}
                        >
                            <PlayerRaceIcon
                                raceId={player.raceId}
                                width={dimensions.playingDetailsRaceIconWidth} // e.g., '24px' or a variable
                                color={player.colours.primaryTextColour} // Or dynamically set based on background: isDark ? 'white' : 'black'
                            />
                        </AttributeBubble>
                    </HelpOverlay>
                </HelpOverlay>
            </AnimatedStack>
        </AnimatedBox>
    );
}

interface PlayingStatsProps {
    player: CalculatedPlayer;
    dimensions: CardDimensions;
    attributeSet: Attribute[];
    showTutorials: boolean;
}

const PlayingStats = (props: PlayingStatsProps) => {
    const { player, dimensions, attributeSet, showTutorials } = props;
    const { combinedBonus, actualLevel } = player;

    const statsToDisplay = Object.entries(combinedBonus.statBlock)
        .filter((entry): entry is [string, number] => typeof entry[1] === 'number' && entry[1] > 0)
        .filter(([stat]) => !attributeSet || attributeSet.includes(stat as Attribute)).sort((a, b) => b[1] - a[1]);

    const levelValue = actualLevel;
    const levelMinSizeValue = 5;
    const levelBaseSizeValue = 4;
    const levelMagnification = Math.pow(Math.max(levelValue, levelMinSizeValue) / levelBaseSizeValue, 0.25);
    const levelFontSize = `calc(${dimensions.playerStatFontSize} * ${levelMagnification})`;
    const levelDiameterCalc = `calc(${levelFontSize} * 2.5)`;

    const levelHelpKey = 'playerCard.level.help';

    return <AnimatedBox
        sx={{
            left: dimensions.playingStatsLeftOffset,
            top: `calc( (${levelDiameterCalc} * -0.5) - ${dimensions.attributeBubbleBorderWidth})`,
            overflow: 'visible',
            position: 'absolute',
        }}
    >
        <AnimatedStack
            spacing={dimensions.playingStatsStackSpacing}
            sx={{
                width: 0,
                alignItems: 'center',
                justifyItems: 'center',
                justifyContent: 'center',
                alignContent: 'center',
                display: 'flex',
                flexDirection: 'column',
            }}>
            <HelpOverlay
                helpText={localise(levelHelpKey)}
                tutorialId={levelHelpKey}
                additionalCondition={showTutorials}
            >
                <StatBubble key='LEVEL' icon='LEVEL' value={actualLevel} minSizeValue={5} baseSizeValue={4} colours={player.colours} dimensions={dimensions} />
            </HelpOverlay>

            {statsToDisplay.map(([stat, value]) => {
                const statHelpKey = `playerCard.attribute.${stat.toUpperCase()}.help`;
                const tutorialId = statHelpKey;
                return (
                    <HelpOverlay
                        key={`${stat}-help`}
                        helpText={localise(statHelpKey)}
                        tutorialId={tutorialId}
                        additionalCondition={showTutorials}
                    >
                        <StatBubble
                            key={stat}
                            value={value}
                            icon={stat.toUpperCase() as IconId}
                            minSizeValue={1}
                            baseSizeValue={4}
                            colours={player.colours}
                            dimensions={dimensions}
                        />
                    </HelpOverlay>
                );
            })}
        </AnimatedStack>
    </AnimatedBox>
}
interface StatBubbleProps {
    value: number,
    icon?: IconId,
    baseSizeValue: number,
    minSizeValue: number,
    colours: Colours,
    dimensions: CardDimensions,
    sx?: any,
}

export const StatBubble = (props: StatBubbleProps) => {
    const { colours, dimensions, value, icon, baseSizeValue, minSizeValue, sx } = props;

    const magnification = Math.pow(Math.max(value, minSizeValue) / baseSizeValue, 0.25);
    const fontSize = `calc(${dimensions.playerStatFontSize} * ${magnification})`;
    const diameter = `calc(${fontSize} * 2.5)`;

    return <AttributeBubble colours={colours} dimensions={dimensions} diameter={diameter} sx={sx}>
        <Typography sx={{
            lineHeight: '1',
            color: colours.primaryTextColour,
            fontSize: fontSize,
            textWrap: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}> <b>{value}</b>
            {icon && <Icon
                iconId={icon}
                fontSize="inherit"
                primaryColor={colours.primaryTextColour}
                secondaryColor={colours.primaryBackgroundColour}
            />}
        </Typography>
    </AttributeBubble>
}

export interface AttributeBubbleProps {
    children: React.ReactNode,
    colours: Colours,
    dimensions: CardDimensions,
    diameter: string,
    sx?: any,
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

export const AttributeBubble = (props: AttributeBubbleProps) => {
    const { colours, dimensions, diameter, children, onClick, sx } = props;
    return <AnimatedCard
        onClick={onClick}
        sx={{
            height: diameter,
            width: diameter,
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: colours.primaryTextColour,
            fontWeight: '400',
            backgroundColor: colours.primaryBackgroundColour,
            border: `${dimensions.attributeBubbleBorderWidth} solid ${colours.primaryColour}`,
            borderRadius: '50%',
            cursor: onClick ? 'pointer' : 'default',
            flexShrink: 0,
            ...sx
        }}
    >
        {children}
    </AnimatedCard>
}

interface ArtworkProps {
    dimensions: CardDimensions;
    player: CalculatedPlayer;
    showTutorials: boolean;
}

const Artwork = (props: ArtworkProps) => {
    const { dimensions, player } = props;
    const avatarComponentsProps: AvatarComponentProps[] = player.avatar ?? [];
    if (avatarComponentsProps.length === 0) {
        avatarComponentsProps.push({
            type: 'placeholder',
            componentName: 'DefaultPlaceholder',
        });
    }

    const avatarComponents = avatarComponentsProps.map((compProps, index) => {
        const key = `${compProps.type}-${compProps.componentName}-${index}`;
        return <Avatar {...compProps} key={key} style={{
            position: 'absolute',
            left: '0',
            top: '0',
            borderRadius: dimensions.artworkAvatarBorderRadius,
            height: dimensions.cardWidth,
            width: dimensions.cardWidth,
        }} />;
    });


    const isArtworkRarer = player.rarity !== Rarity.COMMON;

    return (
        <div style={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
        }}>
            <HelpOverlay
                helpText={localise('playerCard.artwork.rarer.help')}
                tutorialId={'playerCard.artwork.rarer.help'}
                additionalCondition={props.showTutorials && isArtworkRarer}
                textHeight="5vh"
            >
                <AnimatedBox
                    sx={{
                        overflow: 'hidden',
                        borderRadius: dimensions.cardBorderRadius,
                        height: '100%',
                        width: dimensions.cardWidth,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        pointerEvents: 'none'
                    }}
                >

                    {avatarComponents}
                </AnimatedBox>
            </HelpOverlay>
        </div>
    );
};


interface PlayerNameProps {
    id: string;
    colours: Colours;
    dimensions: CardDimensions;
    sx?: any;
}

const PlayerName = (props: PlayerNameProps) => {
    const { dimensions, id, colours, sx: inheritedSx } = props;
    return (
        <AnimatedCard
            sx={{
                backgroundColor: colours.primaryColour,
                width: dimensions.playerNameBackgroundWidth,
                padding: dimensions.playerNameBackgroundPadding,
                justifyItems: 'center',
                ...(inheritedSx ?? {})
            }}
        >
            <Typography
                gutterBottom
                variant="h3"
                component="div"
                sx={{
                    color: colours.primaryTextColour,
                    textAlign: 'center',
                    width: '100%',
                    marginBottom: 0,
                    fontSize: dimensions.playerNameFontSize,
                    fontFamily: IMPACT_FONTS,
                    lineHeight: 1.1,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap'
                }}
            >
                {localise(`player.${id}`)}
            </Typography>
        </AnimatedCard>
    );
};